import { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Box, Card, Grid, Modal, IconButton, Fab, Toolbar, CircularProgress, Typography, Button, Chip } from '@mui/material'
import { Close, ArrowBackIos } from '@mui/icons-material'
import { useParams, useNavigate } from 'react-router-dom'
import { imageToBase64, useStylesTheme } from '../../config'
import { apiIvcRoutes, reportsRoutes } from '../../routes'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCamera, faEye } from '@fortawesome/free-solid-svg-icons'
import { ImageDialog, LoadingLogoDialog, ReportDetailDialog, ImageCropDialog, ConfirmarDialog } from '../../dialogs'
import { GenerationStatusModal } from '../../dialogs/StatusModal'
import { useAuth, useConnectionContext, useExecutionReportContext, useMachineContext, useMasterData, useAlertContext, useStatus } from '../../context'
import { segundosHoras } from '../../utils/NumberUtils'

let cachedFilter = 'Todos'
let cachedMachineId = null

const AppliancePage = ({ route }) => {
    const { id } = useParams()
    const { imagenesGenericasOfLine } = useMasterData()
    const { isOnline } = useConnectionContext()
    const { machineDetail, machineReports, machinesOfProject, machinesBySite, machineSelected, fetchMachineDetailsAndReports } = useMachineContext()
    const { loading, setLoading } = useExecutionReportContext()
    const { setAlertSimple } = useAlertContext()
    const { setStatus } = useStatus()
    const [loadingPautas, setLoadingPautas] = useState(
        !(machineReports && (machineDetail && (machineDetail.equid === id || machineDetail._id === id)))
    )
    const [open, setOpen] = useState(false)
    const [hallazgosData, setHallazgosData] = useState([])
    const [routeData, setRouteData] = useState('')
    const [openDialog, setOpenDialog] = useState(false)
    const [report, setReport] = useState({})
    const [openMachineImage, setOpenMachineImage] = useState(false)
    const [machineImage, setMachineImage] = useState('')
    const [value, setValue] = useState(() => {
        if (cachedMachineId !== id) {
            cachedMachineId = id
            cachedFilter = 'Todos'
        }
        return cachedFilter
    })
    const [machine, setMachine] = useState({})
    const [imageMachine, setImageMachine] = useState('')
    const [rawImageUrl, setRawImageUrl] = useState('')
    const [openCropDialog, setOpenCropDialog] = useState(false)
    const [openConfirmUploadDialog, setOpenConfirmUploadDialog] = useState(false)
    const [pendingCroppedFile, setPendingCroppedFile] = useState(null)
    const [pendingCroppedUrl, setPendingCroppedUrl] = useState('')
    const navigate = useNavigate()

    useEffect(() => {
        if (route === 'inspection') {
            setRouteData('Inspección')
        } else if (route === 'maintenance') {
            setRouteData('Mantención')
        } else if (route === 'machines') {
            setRouteData('Máquinas')
        }
    }, [route])

    useEffect(() => {
        if (cachedMachineId !== id) {
            setValue('Todos')
            cachedFilter = 'Todos'
            cachedMachineId = id
        }
        setHallazgosData([])
        setImageMachine('')
        setLoadingPautas(true)

        // Immediately populate machine data from context if available
        const existingMachine = (machineSelected && (machineSelected.equid === id || machineSelected._id === id))
            ? machineSelected
            : (machinesBySite && machinesBySite.find(m => m.equid === id || m._id === id)) || (machineDetail && machineDetail.equid === id ? machineDetail : null)

        if (existingMachine) {
            setMachine(existingMachine)
        }

        fetchMachineDetailsAndReports(id)
    }, [id])

    useEffect(() => {
        if (machineDetail && (machineDetail.equid === id || machineDetail._id === id)) {
            setMachine(machineDetail)
        }
    }, [machineDetail, id])

    useEffect(() => {
        if (machineReports !== null && machineReports !== undefined) {
            setLoadingPautas(false)
            setLoading(false)
        } else {
            setLoadingPautas(true)
        }
    }, [machineReports])

    const hallazgosReports = useMemo(() => {
        if (loadingPautas || !machineReports) return []
        return machineReports.filter(m => m.hasBacklog || (m.hallazgosCount && m.hallazgosCount > 0))
    }, [machineReports, loadingPautas])

    const filterCounts = useMemo(() => {
        if (loadingPautas || !machineReports) {
            return {
                todos: 0,
                mantenciones: 0,
                inspecciones: 0,
                completados: 0,
                enProceso: 0,
                porAsignar: 0,
                hallazgos: 0
            }
        }

        const reports = machineReports || []
        const mantenciones = reports.filter(m => m.reportType === 'Mantención')
        const inspecciones = reports.filter(m => m.reportType === 'Inspección')
        const completados = reports.filter(m => m.state === 'Completada' || m.state === 'Completadas' || m.state === 'Finalizada')
        const enProceso = reports.filter(m => m.state === 'En proceso')
        const porAsignar = reports.filter(m => m.state === 'Asignar')
        const hallazgos = hallazgosReports

        return {
            todos: reports.length,
            mantenciones: mantenciones.length,
            inspecciones: inspecciones.length,
            completados: completados.length,
            enProceso: enProceso.length,
            porAsignar: porAsignar.length,
            hallazgos: hallazgos.length
        }
    }, [machineReports, hallazgosReports, machineDetail, loadingPautas, id])

    const filteredMantenciones = useMemo(() => {
        if (loadingPautas || loading || !machineReports) {
            return []
        }

        if (value === 'Hallazgo') {
            return [...hallazgosReports].reverse()
        }

        if (!machineReports) return []

        let list = [...machineReports]

        if (value !== 'Todos' && value !== 'all') {
            if (value === 'Mantención' || value === 'Inspección') {
                list = list.filter(m => m.reportType === value)
            } else if (value === 'Completadas' || value === 'Completada') {
                list = list.filter(m => m.state === 'Completada' || m.state === 'Completadas' || m.state === 'Finalizada')
            } else if (value === 'Asignar' || value === 'Asignar') {
                list = list.filter(m => m.state === 'Asignar' || m.state === 'Asignar')
            } else {
                list = list.filter(m => m.state === value)
            }
        }

        return list.reverse()
    }, [machineReports, value, hallazgosReports, loading, loadingPautas, machineDetail, id])

    const getStatusChip = (state) => {
        switch (state) {
            case 'Completada':
            case 'Completadas':
            case 'Finalizada':
                return <Chip label={state} size="small" color="success" sx={{ fontWeight: 'bold', height: 22, fontSize: '0.72rem' }} />
            case 'En proceso':
                return <Chip label={state} size="small" color="primary" sx={{ fontWeight: 'bold', height: 22, fontSize: '0.72rem' }} />
            case 'Asignar':
            case 'Asignar':
                return <Chip label={state === 'Asignar' ? 'Asignar' : state} size="small" sx={{ fontWeight: 'bold', height: 22, fontSize: '0.72rem', bgcolor: '#757575', color: '#ffffff' }} />
            default:
                return <Chip label={state || 'Sin estado'} size="small" variant="outlined" sx={{ fontWeight: 'bold', height: 22, fontSize: '0.72rem' }} />
        }
    }

    const openCloseModal = () => {
        setTimeout(() => {
            setOpen(!open)
        }, 500)
    }

    const initDialog = (report) => {
        setOpenDialog(true)
        setReport(report)
    }

    const handleClose = () => {
        setOpenDialog(false)
    }

    const handleViewPdf = async (reportItem) => {
        if (reportItem && reportItem.urlPdf) {
            try {
                const response = await reportsRoutes.getPdfUrl(reportItem._id)
                if (response.data && response.data.url) {
                    window.open(response.data.url, '_blank')
                } else {
                    alert('No se pudo obtener la URL del PDF. Por favor, intente regenerarlo.')
                }
            } catch (error) {
                console.error('Error fetching PDF URL:', error)
                alert('El enlace del PDF ha expirado o no se pudo renovar. Intente regenerar el PDF desde el detalle.')
            }
        } else {
            alert('El PDF no está disponible. Genérelo desde el detalle del reporte.')
        }
    }

    const [isMopImage, setIsMopImage] = useState(false)

    useEffect(() => {
        if (machine && (machine.model || machine.modelo)) {
            if (machine.urlImagen) {
                setImageMachine(machine.urlImagen)
                setIsMopImage(false)
            } else if (machine.image) {
                setImageMachine(machine.image)
                setIsMopImage(false)
            } else {
                const mop = (machinesOfProject || []).find(p =>
                    (p.modelo === (machine.modelo || machine.model) || p.model === (machine.modelo || machine.model)) &&
                    (!p.brand || !machine.brand || p.brand.toLowerCase() === machine.brand.toLowerCase())
                )
                if (mop?.urlImagen) {
                    setImageMachine(mop.urlImagen)
                    setIsMopImage(true)
                } else if (mop?.image) {
                    setImageMachine(mop.image)
                    setIsMopImage(true)
                } else if (imagenesGenericasOfLine && imagenesGenericasOfLine.length > 0) {
                    setImageMachine(imagenesGenericasOfLine[0].pathBase64)
                    setIsMopImage(false)
                } else {
                    setImageMachine(`../assets/${machine.model || machine.modelo}.png`)
                    setIsMopImage(false)
                }
            }
        }
    }, [machine, machinesOfProject, imagenesGenericasOfLine])

    const filterList = (filterValue) => {
        const selected = filterValue === 'all' ? 'Todos' : filterValue
        setValue(selected)
        cachedFilter = selected
    }

    const openImg = (image) => {
        setMachineImage(image)
        setOpenMachineImage(true)
    }

    const closeImage = () => {
        setOpenMachineImage(false)
    }

    const upImage = () => {
        const input = document.getElementById('fotoMaquina')
        if (input) {
            input.value = ''
            input.click()
        }
    }

    const onImageSelected = (e) => {
        const file = e.target.files && e.target.files[0]
        if (!file) return

        const validTypes = ['image/png', 'image/jpeg', 'image/jpg']
        if (!validTypes.includes(file.type)) {
            setAlertSimple('Formato no válido', 'Por favor, selecciona una imagen con formato PNG, JPG o JPEG.')
            return
        }

        const maxSizeInBytes = 5 * 1024 * 1024 // 5MB
        if (file.size > maxSizeInBytes) {
            setAlertSimple('Archivo demasiado grande', 'La imagen no debe superar los 5MB.')
            return
        }

        const url = URL.createObjectURL(file)
        setRawImageUrl(url)
        setOpenCropDialog(true)
    }

    const handleCropConfirm = (croppedFile, croppedUrl) => {
        setOpenCropDialog(false)
        if (croppedFile) {
            setPendingCroppedFile(croppedFile)
            setPendingCroppedUrl(croppedUrl)
            setOpenConfirmUploadDialog(true)
        }
    }

    const handleExecuteUpload = async () => {
        setOpenConfirmUploadDialog(false)
        if (!pendingCroppedFile) return

        try {
            setStatus({
                status: 'uploading',
                title: 'Subiendo imagen...',
                message: `Guardando la imagen para la máquina ${machine.type || ''} ${machine.brand || ''} ${machine.equ || ''}...`,
                errorDetails: null,
                severity: 'info'
            })

            const res = await imageToBase64(pendingCroppedFile)
            if (res) {
                machine.image = res
                setImageMachine(res)
                setIsMopImage(false)
                await apiIvcRoutes.saveMachineDataById(machine)

                setStatus({
                    status: 'completed',
                    title: 'Imagen Actualizada',
                    message: `La imagen para la máquina ${machine.equ || ''} se guardó exitosamente.`,
                    errorDetails: null,
                    severity: 'success'
                })
            }
        } catch (err) {
            console.error('Error saving machine image:', err)
            setStatus({
                status: 'error',
                title: 'Error al Guardar Imagen',
                message: 'No se pudo guardar la imagen de la máquina. Por favor, intente nuevamente.',
                errorDetails: {
                    code: err.response?.status || 'Cliente',
                    info: err.response?.data?.message || err.message || 'Error desconocido al subir imagen.'
                },
                severity: 'error'
            })
        } finally {
            setPendingCroppedFile(null)
            setPendingCroppedUrl('')
        }
    }

    const hasSpecificImage = Boolean(machine.urlImagen || machine.image)

    return (
        <Box height='100%'>
            <Grid className={'root'} container spacing={0}>
                <Grid className={'pageContainer'} item xs={12}>
                    <Card elevation={0} className={'pageCard'}>
                        <Grid container alignItems='center' justifyContent='center'>
                            <div style={{ width: '100%', textAlign: 'left', padding: 10 }}>
                                <div style={{ width: '100%', textAlign: 'left', color: '#333', backgroundColor: '#fff', borderRadius: 20 }}>
                                    <Toolbar style={{ paddingLeft: 0, backgroundColor: '#F9F9F9', borderRadius: 10 }}>
                                        <IconButton onClick={() => setTimeout(() => {
                                            navigate(-1)
                                        }, 500)}>
                                            <ArrowBackIos style={{ color: '#333', fontSize: 16 }}/>
                                        </IconButton>
                                        <p style={{ marginTop: 0, marginBottom: 0, fontSize: 16 }}>
                                             {routeData}/{machine.type} {machine.brand} {machine.model}/Número interno: <b>{machine.equ}</b>
                                        </p>
                                    </Toolbar>
                                </div>
                            </div>
                            <Grid container>
                                <Grid item xs={12} sm={8} md={6} lg={4} xl={4} >
                                    <div style={{ width: '100%', textAlign: 'left', padding: 10 }}>
                                        <div style={{ padding: 15, borderTopLeftRadius: 20, borderEndStartRadius: 20, backgroundColor: '#F9F9F9', borderRadius: 10, minHeight: 400 }}>
                                            <h3 style={{ marginTop: 5, marginBottom: 5 }}>{machine.type}</h3>
                                            <div style={{ width: '100%', backgroundColor: 'transparent', textAlign: 'center' }}>
                                                <img
                                                    src={imageMachine}
                                                    height={180}
                                                    id={'imageMachine'}
                                                    style={{
                                                        opacity: isMopImage ? 0.5 : 1,
                                                        transition: 'opacity 0.3s',
                                                        cursor: 'pointer',
                                                        maxWidth: '100%',
                                                        objectFit: 'contain'
                                                    }}
                                                    onClick={() => { openImg(imageMachine || (machine.image ? machine.image : `/assets/${machine.model}.png`)) }}
                                                />
                                                {isMopImage && (
                                                    <Typography variant="caption" display="block" sx={{ color: 'text.secondary', fontStyle: 'italic', mt: 0.5, mb: 1, fontSize: '0.75rem' }}>
                                                        Imagen genérica del modelo.<br />Puedes subir una foto específica para esta máquina.
                                                    </Typography>
                                                )}
                                                <br />
                                                <button onClick={() => { upImage() }}>
                                                    <FontAwesomeIcon icon={faCamera} style={{ marginRight: 10 }} /> {hasSpecificImage ? 'Cambiar imagen' : 'Agregar foto'}
                                                </button>
                                                <input autoComplete="off" type="file" id="fotoMaquina" accept="image/png,image/jpeg,image/jpg" onChange={onImageSelected} hidden />
                                            </div>
                                            <div style={{ width: '100%' }}>
                                                <p style={{ marginTop: 5, marginBottom: 5 }} id={'machineData'}><b>Marca: </b> {machine.brand} </p>
                                            </div>
                                            <div style={{ width: '100%' }}>
                                                <p style={{ marginTop: 5, marginBottom: 5 }}><b>Modelo: </b> {machine.model} </p>
                                            </div>
                                            <div style={{ width: '100%' }}>
                                                <p style={{ marginTop: 5, marginBottom: 5 }}><b>Código: </b> {machine.equid} </p>
                                            </div>
                                            <div style={{ width: '100%' }}>
                                                <p style={{ marginTop: 5, marginBottom: 5 }}><b>Horómetro Actual: </b> {segundosHoras(machine.hourMeter)} hrs </p>
                                            </div>
                                            <div style={{ width: '100%' }}>
                                                <p style={{ marginTop: 5, marginBottom: 5 }}><b>Número Interno: </b> {machine.equ} </p>
                                            </div>
                                        </div>
                                    </div>
                                </Grid>
                                <Grid item sm={12} md={7} lg={8} xl={8} >
                                    <div style={{ width: '100%', textAlign: 'left', padding: 10 }}>
                                        <div style={{ padding: 15, backgroundColor: '#F9F9F9', width: '100%', height: 'calc(100vh - 220px)', position: 'relative', borderRadius: 10 }}>
                                            <div style={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                                                <Button
                                                    size="small"
                                                    variant={value === 'Mantención' ? 'contained' : 'outlined'}
                                                    color="primary"
                                                    onClick={() => filterList('Mantención')}
                                                    sx={{ textTransform: 'none', borderRadius: 2, fontSize: '0.78rem', px: 1.5, py: 0.5 }}
                                                >
                                                    Mantenciones ({filterCounts.mantenciones})
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant={value === 'Inspección' ? 'contained' : 'outlined'}
                                                    color="primary"
                                                    onClick={() => filterList('Inspección')}
                                                    sx={{ textTransform: 'none', borderRadius: 2, fontSize: '0.78rem', px: 1.5, py: 0.5 }}
                                                >
                                                    Inspecciones ({filterCounts.inspecciones})
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant={(value === 'Todos' || value === 'all') ? 'contained' : 'outlined'}
                                                    color="primary"
                                                    onClick={() => filterList('all')}
                                                    sx={{ textTransform: 'none', borderRadius: 2, fontSize: '0.78rem', px: 1.5, py: 0.5 }}
                                                >
                                                    Todos ({filterCounts.todos})
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant={(value === 'Completada' || value === 'Completadas') ? 'contained' : 'outlined'}
                                                    color="primary"
                                                    onClick={() => filterList('Completadas')}
                                                    sx={{ textTransform: 'none', borderRadius: 2, fontSize: '0.78rem', px: 1.5, py: 0.5 }}
                                                >
                                                    Completados ({filterCounts.completados})
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant={value === 'En proceso' ? 'contained' : 'outlined'}
                                                    color="primary"
                                                    onClick={() => filterList('En proceso')}
                                                    sx={{ textTransform: 'none', borderRadius: 2, fontSize: '0.78rem', px: 1.5, py: 0.5 }}
                                                >
                                                    En proceso ({filterCounts.enProceso})
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant={(value === 'Asignar' || value === 'Asignar') ? 'contained' : 'outlined'}
                                                    color="primary"
                                                    onClick={() => filterList('Asignar')}
                                                    sx={{ textTransform: 'none', borderRadius: 2, fontSize: '0.78rem', px: 1.5, py: 0.5 }}
                                                >
                                                    Asignar ({filterCounts.porAsignar})
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant={value === 'Hallazgo' ? 'contained' : 'outlined'}
                                                    color={value === 'Hallazgo' ? 'error' : 'warning'}
                                                    onClick={() => filterList('Hallazgo')}
                                                    sx={{
                                                        textTransform: 'none',
                                                        borderRadius: 2,
                                                        fontSize: '0.78rem',
                                                        px: 1.5,
                                                        py: 0.5,
                                                        fontWeight: 'bold',
                                                        bgcolor: value === 'Hallazgo' ? undefined : '#fff3e0',
                                                        borderColor: '#ffb74d',
                                                        color: value === 'Hallazgo' ? '#fff' : '#e65100',
                                                        '&:hover': {
                                                            bgcolor: value === 'Hallazgo' ? undefined : '#ffe0b2'
                                                        }
                                                    }}
                                                >
                                                    Hallazgos ({filterCounts.hallazgos})
                                                </Button>
                                            </div>
                                            <div style={{ overflowY: 'auto', height: 'calc(100vh - 300px)', width: '100%', padding: 20, marginTop: 50, }}>
                                                {
                                                    loadingPautas ? (
                                                        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="200px" gap={2}>
                                                            <CircularProgress color="primary" />
                                                            <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500 }}>
                                                                Cargando pautas...
                                                            </Typography>
                                                        </Box>
                                                    ) : (
                                                        <>
                                                            {
                                                                (isOnline && filteredMantenciones.length === 0) && <p>No hay mantenciones</p>
                                                            }
                                                            {
                                                                (isOnline && filteredMantenciones.length > 0) && filteredMantenciones.map((mantencion, index) => {
                                                                    const displayId = mantencion.newIdIndex || mantencion.idIndex || mantencion._id
                                                                    if (!mantencion || !displayId) {
                                                                        return null
                                                                    }
                                                                    const progress = mantencion.progress !== undefined ? mantencion.progress : 0
                                                                    const itemHallazgos = mantencion.hallazgosCount || mantencion.backlogCount || (mantencion.itemListWithBacklog ? mantencion.itemListWithBacklog.length : 0)

                                                                    return (
                                                                        <div
                                                                            key={displayId}
                                                                            style={{
                                                                                width: '98%',
                                                                                padding: 12,
                                                                                margin: '10px 0',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'space-between',
                                                                                borderRadius: 10,
                                                                                backgroundColor: '#ffffff',
                                                                                border: '1px solid #e0e0e0',
                                                                                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                                                                            }}
                                                                        >
                                                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1565c0', fontSize: '0.95rem' }}>
                                                                                        OT N°{displayId}
                                                                                    </Typography>
                                                                                    {mantencion.reportType && (
                                                                                        <Chip label={mantencion.reportType} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                                                                                    )}
                                                                                    {getStatusChip(mantencion.state)}
                                                                                    <Chip
                                                                                        label={`Progreso: ${progress.toFixed(0)}%`}
                                                                                        size="small"
                                                                                        variant="outlined"
                                                                                        color="info"
                                                                                        sx={{ height: 20, fontSize: '0.7rem' }}
                                                                                    />
                                                                                    <Chip
                                                                                        label={`Hallazgos: ${itemHallazgos}`}
                                                                                        size="small"
                                                                                        color={itemHallazgos > 0 ? "warning" : "default"}
                                                                                        variant={itemHallazgos > 0 ? "filled" : "outlined"}
                                                                                        sx={{ height: 20, fontSize: '0.7rem', fontWeight: itemHallazgos > 0 ? 'bold' : 'normal' }}
                                                                                    />
                                                                                </Box>
                                                                                <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem' }}>
                                                                                    Código SAP: <b>{mantencion.sapId || '-'}</b>
                                                                                </Typography>
                                                                                {
                                                                                    (mantencion.state === 'Completada' || mantencion.state === 'Completadas') && mantencion.urlPdf && (
                                                                                        <Button
                                                                                            size="small"
                                                                                            variant="text"
                                                                                            color="primary"
                                                                                            onClick={() => handleViewPdf(mantencion)}
                                                                                            sx={{ p: 0, justifyContent: 'flex-start', minWidth: 'auto', fontSize: '0.78rem', textTransform: 'none', textDecoration: 'underline' }}
                                                                                        >
                                                                                            Imprimir OT
                                                                                        </Button>
                                                                                    )
                                                                                }
                                                                            </Box>
                                                                            <IconButton
                                                                                color="primary"
                                                                                onClick={() => initDialog(mantencion)}
                                                                            >
                                                                                <FontAwesomeIcon icon={faEye} size="xs" />
                                                                            </IconButton>
                                                                        </div>
                                                                    )
                                                                })
                                                            }
                                                            {
                                                                !isOnline && <p>Debe contar con conexión a internet.</p>
                                                            }
                                                        </>
                                                    )
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </Grid>
                                <Modal
                                    open={open}
                                    onClose={openCloseModal}
                                    aria-labelledby="modal-modal-title"
                                    aria-describedby="modal-modal-description"
                                >
                                    <div style={{ height: '100%', width: '100%', backgroundColor: '#333' }}>
                                        <Fab onClick={openCloseModal} style={{ position: 'absolute', right: 10, top: 10, boxShadow: 'none', backgroundColor: 'transparent' }}>
                                            <Close style={{ color: '#ccc' }} />
                                        </Fab>
                                    </div>
                                </Modal>
                                {
                                    openDialog && <ReportDetailDialog open={openDialog} report={report} handleClose={handleClose}/>
                                }
                                {
                                    loading && <LoadingLogoDialog open={loading} />
                                }
                                {
                                    openMachineImage && <ImageDialog open={openMachineImage} image={machineImage} handleClose={closeImage}/>
                                }
                                <ImageCropDialog
                                    open={openCropDialog}
                                    imageUrl={rawImageUrl}
                                    onClose={() => setOpenCropDialog(false)}
                                    onConfirm={handleCropConfirm}
                                />
                                <ConfirmarDialog
                                    open={openConfirmUploadDialog}
                                    message={`¿Estás seguro de que deseas guardar esta nueva imagen para la máquina ${machine.equ || ''}?`}
                                    handleClose={() => {
                                        setOpenConfirmUploadDialog(false)
                                        setPendingCroppedFile(null)
                                        setPendingCroppedUrl('')
                                    }}
                                    select={handleExecuteUpload}
                                />
                                <GenerationStatusModal />
                            </Grid>
                        </Grid>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    )
}

AppliancePage.propTypes = {
    route: PropTypes.oneOf(['machines/machine-detail'])
}

export default AppliancePage
